import { useEffect, useRef, useSyncExternalStore, useState } from "react";
import { CloudUpload, Loader2, Server } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBytes } from "@/components/OneDriveQuota";

let state = { status: "idle" };
const NAME = { onedrive: "OneDrive", filejump: "FileJump" };
const destName = (s) => NAME[s?.dest] || "OneDrive";
const subs = new Set();
let timer = null;
const emit = (s) => {
  const prev = state;
  state = s;
  subs.forEach((f) => f());
  if (prev.status === "running" && s.status !== "running" && s.job === prev.job) {
    if (s.status === "done" && !s.failed) toast.success(`Moved ${s.done} song${s.done === 1 ? "" : "s"} to ${destName(s)}`, { description: `${fmtBytes(s.freed)} freed in Resonance storage` });
    else toast.error(`${s.failed || "Some"} song${s.failed === 1 ? "" : "s"} couldn't be moved`, { description: (s.errors || [])[0] });
  }
};
const schedule = () => {
  if (state.status === "running" && !timer) timer = setTimeout(() => { timer = null; refreshMove(); }, 1500);
};
export const refreshMove = async () => {
  try { const { data } = await api.get("/storage/move"); emit(data); schedule(); } catch { /* ignore */ }
};
export const startMove = async (body) => {
  const { data } = await api.post("/storage/move", body);
  emit(data);
  schedule();
  return data;
};
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };

export const useMoveStatus = () => {
  const s = useSyncExternalStore(subscribe, () => state);
  useEffect(() => { refreshMove(); }, []);
  return s;
};

export const useOnMoveDone = (fn) => {
  const s = useMoveStatus();
  const seen = useRef(s.job);
  useEffect(() => {
    if (s.status === "done" && s.job !== seen.current) { seen.current = s.job; fn(s); }
    if (s.status === "running") seen.current = null;
  }, [s, fn]);
  return s;
};

export const MoveButton = ({ dest = "onedrive", albumIds, all = false, label, size = "md", testid }) => {
  const s = useMoveStatus();
  const [busy, setBusy] = useState(false);
  const running = s.status === "running" || busy;
  const name = NAME[dest];
  const go = async () => {
    if (busy) return;
    if (all && !window.confirm(`Move every song in your Resonance storage into your ${name} music folder? They'll play from ${name} afterwards.`)) return;
    setBusy(true);
    try {
      const d = await startMove({ ...(all ? { all: true } : { album_ids: albumIds }), dest });
      toast(`Moving ${d.total} song${d.total === 1 ? "" : "s"} to ${name}…`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const cls = size === "sm" ? "h-8 px-3 text-xs" : "h-12 px-5 text-sm";
  const tone = dest === "filejump" ? "border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-700" : "border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-700";
  const Icon = dest === "filejump" ? Server : CloudUpload;
  return (
    <button onClick={go} disabled={running} data-testid={testid || `move-to-${dest}`} title={`Copy into your ${name} music folder and free Resonance storage`}
      className={`flex items-center gap-2 rounded-full border transition-colors disabled:opacity-50 ${tone} ${cls}`}>
      {running ? <Loader2 size={size === "sm" ? 12 : 14} className="animate-spin" /> : <Icon size={size === "sm" ? 12 : 14} />}
      {running ? `Moving ${s.done + s.failed}/${s.total}…` : label || `Move to ${name}`}
    </button>
  );
};

export const MoveToOneDriveButton = (props) => <MoveButton dest="onedrive" {...props} />;

/* Primary owner → FileJump (primary storage); everyone else keeps OneDrive. */
export const MoveOutButton = ({ all, albumIds, size, testidPrefix = "move", label }) => {
  const { user } = useAuth();
  const fj = !!user?.permissions?.filejump;
  const dest = fj ? "filejump" : "onedrive";
  const text = label || (all ? `Move everything to ${NAME[dest]}` : undefined);
  return <MoveButton dest={dest} all={all} albumIds={albumIds} size={size} label={text} testid={`${testidPrefix}-to-${dest}`} />;
};

export const MoveProgress = () => {
  const s = useMoveStatus();
  if (s.status === "idle" || (s.status !== "running" && !s.errors?.length)) return null;
  const pct = s.total ? Math.round(((s.done + s.failed) / s.total) * 100) : 0;
  return (
    <div className="mt-4 rounded-md bg-sky-50 px-4 py-3 text-xs text-sky-900" data-testid="move-progress">
      {s.status === "running" ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin" />Moving to {destName(s)} · {s.done + s.failed} of {s.total}</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sky-100"><div className="h-full rounded-full bg-sky-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          {s.current && <p className="mt-1.5 truncate text-sky-700" data-testid="move-current">{s.current}</p>}
        </>
      ) : (
        <div data-testid="move-errors">
          <p className="font-medium text-red-700">{s.failed} song{s.failed === 1 ? "" : "s"} couldn't be moved</p>
          {s.errors.map((e, i) => <p key={i} className="mt-0.5 truncate text-red-700/80">{e}</p>)}
        </div>
      )}
    </div>
  );
};
